import { Injectable } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { OpsContact, OpsStatus } from './ops-presence.model';
import { ChatService } from '../../../core/chat/chat.service';
import { ChatSummaryDto } from '../../../core/chat/chat.dto';
import { ManagementService } from '../../../pages/management/management.service';

export type OpsMessageFailure = 'NO_FUND_MAPPING';

export interface OpsMessageError {
  reason: OpsMessageFailure;
}

/** The stable half of a contact — everything Teams presence does not touch. */
type OpsIdentity = Omit<OpsContact, 'status' | 'statusNote'>;

/**
 * Source of ops-team presence for the portal header.
 *
 * MOCK — presence is generated locally and rerolled on a timer so the header
 * visibly behaves like a live feed. The method is the seam for the real
 * integration and is already shaped for it:
 *
 *   getOpsTeam()  -> GET /gp-portal/v1/vehicles/:vehicleId/ops-presence
 *                    (or a socket/SSE stream — the component only ever sees an
 *                     Observable that keeps emitting, so either works). The
 *                     backend polls Microsoft Graph presence for each contact's
 *                     email and collapses Teams availability into OpsStatus.
 * Swapping in the real call should not require touching the component: keep the
 * return type and drop everything below `identities`.
 */
@Injectable({
  providedIn: 'root',
})
export class OpsPresenceService {
  constructor(
    private chatService: ChatService,
    private managementService: ManagementService
  ) {}

  // Stand-in for the network round trip, so the header renders its loading
  // state the same way it will against the real endpoint.
  private static readonly MOCK_LATENCY_MS = 400;

  // How often the mock rerolls presence. Slow enough to read, fast enough that
  // the dynamism is obvious in a demo.
  private static readonly MOCK_REROLL_MS = 6000;

  /**
   * Real people, placeholder roles — the backend will supply the role each
   * person actually holds on the fund.
   */
  private readonly identities: OpsIdentity[] = [
    {
      id: 'julian.suchsland',
      name: 'Julian Suchsland',
      email: 'julian.suchsland@ace-alternatives.com',
      role: 'Fund Accounting',
      officeHours: { days: 'Mon–Fri, 09:00–18:00', timezone: 'CET' },
    },
    {
      id: 'farman.bakhshaliyev',
      name: 'Farman Bakhshaliyev',
      email: 'farman.bakhshaliyev@ace-alternatives.com',
      role: 'Fund Operations',
      officeHours: { days: 'Mon–Fri, 08:00–17:00', timezone: 'CET' },
    },
    {
      id: 'omar.ahmad',
      name: 'Omar Ahmad',
      email: 'Omar.Ahmad@ace-alternatives.com',
      role: 'Portfolio Operations',
      officeHours: { days: 'Mon–Thu, 10:00–19:00', timezone: 'CET' },
    },
  ];

  // Notes that plausibly accompany each status, so a reroll doesn't leave a
  // green bubble explaining that someone is out of office. `undefined` entries
  // are the "no note" case.
  private static readonly STATUS_NOTES: Record<
    OpsStatus,
    (string | undefined)[]
  > = {
    active: [undefined, undefined, 'Available for a call'],
    away: [
      'In a meeting until 14:00',
      'Back in 15 minutes',
      'On another call',
      undefined,
    ],
    off: ['Outside office hours', 'Out of office until Mon 8 Sep', 'Offline'],
  };

  // Weighted so most of the team is usually reachable — a uniform roll makes
  // the header look permanently unstaffed.
  private static readonly STATUS_WEIGHTS: OpsStatus[] = [
    'active',
    'active',
    'active',
    'away',
    'away',
    'off',
  ];

  private opsTeam$?: Observable<OpsContact[]>;

  /**
   * The ops people assigned to the currently selected vehicle. Emits once after
   * a simulated round trip, then re-emits a fresh roll every MOCK_REROLL_MS —
   * the component just re-renders whatever arrives, exactly as it will when a
   * real feed drives this.
   */
  getOpsTeam(): Observable<OpsContact[]> {
    if (!this.opsTeam$) {
      this.opsTeam$ = timer(
        OpsPresenceService.MOCK_LATENCY_MS,
        OpsPresenceService.MOCK_REROLL_MS
      ).pipe(
        map(() => this.identities.map(identity => this.roll(identity))),
        // One timer no matter how many places show the cluster, and late
        // subscribers get the current state rather than waiting for a reroll.
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.opsTeam$;
  }

  private roll(identity: OpsIdentity): OpsContact {
    const status = this.pick(OpsPresenceService.STATUS_WEIGHTS);
    return {
      ...identity,
      status,
      statusNote: this.pick(OpsPresenceService.STATUS_NOTES[status]),
    };
  }

  private pick<T>(from: T[]): T {
    return from[Math.floor(Math.random() * from.length)];
  }

  messageOpsTeam(
    vehicleId: string,
    gpId: string | null,
    body: string
  ): Observable<ChatSummaryDto> {
    return this.managementService
      .resolveAceFund(vehicleId, gpId ?? undefined)
      .pipe(
        map(fund => fund?.id?.trim() ?? ''),
        switchMap(fundId => {
          if (!fundId) {
            const failure: OpsMessageError = { reason: 'NO_FUND_MAPPING' };
            return throwError(() => failure);
          }
          return this.findOrCreateSupportChat(fundId);
        }),
        switchMap(chat =>
          this.chatService.sendMessage(chat.id, { body }).pipe(map(() => chat))
        )
      );
  }

  private findOrCreateSupportChat(fundId: string): Observable<ChatSummaryDto> {
    return this.chatService.listChats({ fundId }).pipe(
      map(response =>
        (response?.items ?? []).find(
          chat => chat.type === 'SUPPORT' && !chat.isArchived
        )
      ),
      switchMap(existing =>
        existing ? of(existing) : this.chatService.createSupportChat({ fundId })
      )
    );
  }
}
